import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { loadOrgUsers } from '../../action/org/load-org-users';
import { userSelector } from '../../reducers/current-user-selectors';
import { usersForOrgIdSelector } from '../../reducers/user-selectors';
import { createNotificationSetting } from '../actions/create-notification-setting';
import { deleteNotificationSetting } from '../actions/delete-notification-setting';
import { loadNotificationSettings } from '../actions/load-notification-settings';
import { notificationSettingsBySubscribedToOrgIdSelector } from '../notification-settings-selectors';

import { UiGreenSwitch } from '../../ui/ui-green-switch';

import { NOTIFICATION_TYPES } from '../../util/notification-types';
import { SORT_ORDER, sortAlphabetically } from '../../util/sort-functions';

interface InputProps {
  orgId: number;
}

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  currentUser: userSelector(state),
  users: usersForOrgIdSelector(state, ownProps.orgId),
  notificationSettings: notificationSettingsBySubscribedToOrgIdSelector(state, ownProps.orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadUsers: () => dispatch(loadOrgUsers(ownProps.orgId)),
    createNotificationSettings: (username: string) =>
      dispatch(
        createNotificationSetting({
          username,
          subscribedToOrgId: ownProps.orgId,
          notificationType: NOTIFICATION_TYPES.WEEK_IN_REVIEW,
        }),
      ),
    deleteNotificationSettings: (notificationSettingsId: number) =>
      dispatch(deleteNotificationSetting(notificationSettingsId)),
    loadNotificationSettings: () =>
      dispatch(
        loadNotificationSettings({
          subscribedToOrgId: ownProps.orgId,
          notificationType: NOTIFICATION_TYPES.WEEK_IN_REVIEW,
        }),
      ),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = InputProps & StateProps & DispatchProps;

export const WeekInReviewNotifications = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { actions, users, notificationSettings } = props;

  useEffect(() => {
    actions.loadUsers();
    actions.loadNotificationSettings();
  });

  if (!users || !notificationSettings) {
    return null;
  }

  const handleUpdateWeekInReviewNotificationSettings =
    (username: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      if (checked) {
        actions.createNotificationSettings(username);
      } else {
        const notificationSetting = notificationSettings.find((ns) => ns.username === username);
        if (notificationSetting) {
          actions.deleteNotificationSettings(notificationSetting.notificationSettingId);
        }
      }
    };

  return (
    <div className="u-maxWidth-800px u-padding-top-2x">
      <div id="week-in-review-subscription" className="typo-h5 typo-bold">
        Week in Review Email
      </div>
      <div className="u-margin-bottom-xl">
        The Week in Review is an email sent at the end of the week that highlights what was
        accomplished in Team Engine.
      </div>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell align="center">Subscribed to Week in Review</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users
            .filter((u) => u.isActive)
            .sort(sortAlphabetically((u) => u.firstName, SORT_ORDER.ASC))
            .map((u) => (
              <TableRow key={`user-notifications-${u.username}`}>
                <TableCell>
                  {u.firstName} {u.lastName}
                </TableCell>
                <TableCell align="center">
                  <UiGreenSwitch
                    checked={notificationSettings.some((ns) => ns.username === u.username)}
                    onChange={handleUpdateWeekInReviewNotificationSettings(u.username)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
});
